import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { getInitials } from "../lib/initials";
import { FOCUS_RING } from "../lib/focus";
import { cn } from "../lib/utils";
import { Icon, type IconComponent, type IconSize } from "./icon";
import { PinnableTooltip, type TooltipSide } from "./tooltip";

/**
 * v2 Avatar + AvatarGroup (redesign phase 5, #455). Storybook only until
 * implementation; see docs/design-library/04-implementation-notes.md.
 *
 * Props instead of Radix parts (break #113): `name` drives the initials and
 * the accessible name, `src` an image, `icon` replaces the initials (an
 * outbound message, the assistant). Four sizes, 20 / 24 / 32 / 40; the
 * initials never go below 11px.
 *
 * Colour: `colorSlot` picks the person's slot in `AVATAR_PALETTE`, so a team
 * tells its people apart at a glance. The slot is stored per person (assigned
 * once, evenly, never changed), never hashed from the name, so nobody's colour
 * moves when someone joins. The palette is the brand scales' light and mid
 * tones with navy or white initials at AA contrast or better. Without
 * `colorSlot` the neutral `--muted` fill with `--ink-muted` text
 * (organisations, the assistant).
 *
 * Shape: a circle is a person, a square (radius 4 / 6) an organisation.
 *
 * Image: Radix loads it first; a broken image falls back to the initials.
 * With `src` the fallback waits 300ms so fast images don't flash initials;
 * without `src` the initials show at once.
 *
 * Semantics: decorative by default (it sits next to the visible name):
 * `aria-hidden`, the image `alt=""`. Alone, `decorative={false}` makes it
 * `role="img"` named `name`; `tooltip` puts it on a pinnable tooltip trigger
 * (hover / focus opens, click pins) that shows the name.
 */

export type AvatarSize = "xs" | "sm" | "md" | "lg";

/** Fill and initials of each avatar colour slot (DESIGN.md brand scales). Its length is the
 * server's palette size (migration `user_avatar_columns`). */
export const AVATAR_PALETTE: readonly { fill: string; ink: string }[] = [
  { fill: "#AEBDF9", ink: "#07195F" }, // Deep Blue 2
  { fill: "#99E9FC", ink: "#07195F" }, // Light Blue 4
  { fill: "#EEC6F5", ink: "#07195F" }, // Purple 2
  { fill: "#F7F99E", ink: "#07195F" }, // Yellow 4
  { fill: "#39477F", ink: "#FFFFFF" }, // Dark Blue 4
  { fill: "#DD8DEA", ink: "#07195F" }, // Purple 4
  { fill: "#4EA3B7", ink: "#07195F" }, // Light Blue 7
  { fill: "#1F368F", ink: "#FFFFFF" }, // Deep Blue 7
];
export type AvatarShape = "circle" | "square";

const SIZE: Record<AvatarSize, { box: string; text: string; icon: IconSize; square: string }> = {
  xs: { box: "size-5", text: "text-micro", icon: 12, square: "rounded-sm" },
  sm: { box: "size-6", text: "text-micro", icon: 12, square: "rounded-sm" },
  md: { box: "size-8", text: "text-caption font-semibold", icon: 16, square: "rounded-md" },
  lg: { box: "size-10", text: "text-label", icon: 20, square: "rounded-md" },
};

/** Radix waits this long for an image before showing the initials. */
const IMAGE_FALLBACK_DELAY = 300;

/** The trigger look for an Avatar or AvatarGroup on a pinnable tooltip. */
const TRIGGER = `inline-flex shrink-0 rounded-full ${FOCUS_RING}`;

export interface AvatarProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  /** The person's or organisation's name: initials, image alt and accessible name. */
  name: string;
  src?: string;
  /** Replaces the initials, e.g. `Bot`, `UserRoundPen`. */
  icon?: IconComponent;
  size?: AvatarSize;
  /** `square` for organisations. */
  shape?: AvatarShape;
  /** The person's colour slot (index into `AVATAR_PALETTE`); neutral without one. */
  colorSlot?: number | null;
  /** Default true: next to a visible name. False: `role="img"` named `name`. */
  decorative?: boolean;
  /** Show `name` in a tooltip (only when not decorative). */
  tooltip?: boolean;
  tooltipSide?: TooltipSide;
}

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  {
    name,
    src,
    icon,
    size = "md",
    shape = "circle",
    colorSlot,
    decorative = true,
    tooltip = false,
    tooltipSide,
    className,
    style,
    ...props
  },
  ref,
) {
  const s = SIZE[size];
  const slot = colorSlot == null ? undefined : AVATAR_PALETTE[colorSlot % AVATAR_PALETTE.length];
  const avatar = (
    <AvatarPrimitive.Root
      ref={ref}
      data-size={size}
      data-shape={shape}
      aria-hidden={decorative || undefined}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : name}
      className={cn(
        "relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden bg-muted align-middle font-sans text-ink-muted",
        s.box,
        s.text,
        shape === "circle" ? "rounded-full" : s.square,
        className,
      )}
      style={slot ? { backgroundColor: slot.fill, color: slot.ink, ...style } : style}
      {...props}
    >
      {src && <AvatarPrimitive.Image src={src} alt="" className="size-full object-cover" />}
      <AvatarPrimitive.Fallback
        delayMs={src ? IMAGE_FALLBACK_DELAY : undefined}
        className="flex size-full items-center justify-center"
      >
        {icon ? (
          <Icon icon={icon} size={s.icon} />
        ) : (
          getInitials(name, { kind: shape === "square" ? "organisation" : "person" })
        )}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );

  if (decorative || !tooltip) return avatar;
  return (
    // The tooltip repeats the name, which is already the button's name: no description.
    <PinnableTooltip
      content={name}
      side={tooltipSide}
      aria-describedby={undefined}
      className={cn(TRIGGER, shape === "square" && s.square)}
    >
      {avatar}
    </PinnableTooltip>
  );
});

export interface AvatarGroupPerson {
  name: string;
  src?: string;
  colorSlot?: number | null;
}

export interface AvatarGroupLabels {
  /** The group's accessible name. Default: "Anna Becker, Jonas Weiß und 2 weitere". */
  summary?: (shown: string[], hidden: number) => string;
}

export interface AvatarGroupProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  people: AvatarGroupPerson[];
  /** Avatars before the "+N". Default 3. */
  max?: number;
  size?: AvatarSize;
  /** The surface under the group: the ring that separates the faces takes its colour. */
  surface?: "page" | "card" | "popover" | "selected";
  labels?: AvatarGroupLabels;
  /** One pinnable tooltip listing every name. Not inside a ListItem: its row link covers the group. */
  tooltip?: boolean;
  tooltipSide?: TooltipSide;
}

const RING: Record<NonNullable<AvatarGroupProps["surface"]>, string> = {
  page: "ring-background",
  card: "ring-card",
  popover: "ring-popover",
  selected: "ring-primary-subtle",
};

function defaultSummary(shown: string[], hidden: number): string {
  if (hidden > 0) return `${shown.join(", ")} und ${hidden} weitere`;
  if (shown.length < 2) return shown.join("");
  return `${shown.slice(0, -1).join(", ")} und ${shown[shown.length - 1]}`;
}

/**
 * Stacked people (break #114), e.g. who is viewing a ticket. Faces sit edge
 * to edge, each with a 2px ring in the surface colour that cuts a gap into
 * the face before it. No overlap: Sora "AB" at 11px is ~16px wide, so even a
 * 2px overlap put the second initial of a 20px face under the ring (seen in
 * Storybook). After `max` a "+N" disc of
 * the same size. The group is one `role="img"` named by the names. With
 * `tooltip` it is one pinnable trigger whose tooltip lists every name (a
 * tooltip per face would add a tab stop per face).
 */
const AvatarGroup = React.forwardRef<HTMLSpanElement, AvatarGroupProps>(function AvatarGroup(
  { people, max = 3, size = "xs", surface = "card", labels, tooltip = false, tooltipSide, className, ...props },
  ref,
) {
  const shown = people.slice(0, max);
  const hidden = people.length - shown.length;
  const summary = (labels?.summary ?? defaultSummary)(
    shown.map((p) => p.name),
    hidden,
  );
  const ring = cn("ring-2", RING[surface]);

  const group = (
    <span
      ref={ref}
      role="img"
      aria-label={summary}
      className={cn("inline-flex shrink-0 items-center", className)}
      {...props}
    >
      {shown.map((person, i) => (
        <Avatar key={`${person.name}-${i}`} name={person.name} src={person.src} colorSlot={person.colorSlot} size={size} className={ring} />
      ))}
      {hidden > 0 && (
        <span
          aria-hidden="true"
          data-slot="overflow"
          className={cn(
            "relative inline-flex shrink-0 items-center justify-center rounded-full bg-muted font-sans text-ink-muted tabular-nums",
            SIZE[size].box,
            SIZE[size].text,
            ring,
          )}
        >
          +{hidden}
        </span>
      )}
    </span>
  );

  if (!tooltip) return group;
  return (
    <PinnableTooltip
      content={people.map((p) => p.name).join(", ")}
      side={tooltipSide}
      aria-describedby={undefined}
      className={TRIGGER}
    >
      {group}
    </PinnableTooltip>
  );
});

export { Avatar, AvatarGroup };
